import React, { useRef, useState, useEffect } from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import Pagination from "../Pagination";
import AppLoader from "../AppLoader";
import AppSwitchButton from "../AppSwitchButton";
import AppButton from "../AppButton";
import { Competitor } from "../../../services/getCompetitors";

interface Column<T> {
  id: string;
  label: string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
  /** If true, cell content wraps instead of truncating with ellipsis (e.g. for product names) */
  wrapText?: boolean;
  /** Optional title for the cell (e.g. full text for hover when content is wrapped) */
  cellTitle?: (item: T) => string;
}

interface AppTableProps<T> {
  title?: string;
  columns: Column<T>[];
  renderRowKey: (item: T) => number;
  rows: T[];
  emptyText?: string;
  loading: boolean;
  error: string | null;
  count: number;
  page: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isRowClickable?: boolean;
  onRowClick?: (row: T) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  globalToggleChecked?: boolean;
  onGlobalToggleChange?: (checked: boolean) => void;
  handleEnableScraping?: () => void;
  filterType?: "all" | "not_cheapest" | "price_changes" | "watchlisted";
  onFilterChange?: (
    value: "all" | "not_cheapest" | "price_changes" | "watchlisted",
  ) => void;
  onSort?: (columnId: string) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  lastRunDate?: string | null;
  onCellClick?: (columnId: string, value: any, row: T) => void;
  competitors?: Competitor[];
  selectedCompetitors?: string[];
  onCompetitorChange?: (values: string[]) => void;
}

function AppTable<T>({
  title,
  columns,
  renderRowKey,
  rows,
  emptyText = "No records found",
  loading,
  error,
  count,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  isRowClickable = false,
  onRowClick,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search…",
  globalToggleChecked,
  onGlobalToggleChange,
  handleEnableScraping,
  filterType,
  onFilterChange,
  onSort,
  sortBy,
  sortOrder,
  lastRunDate,
  onCellClick,
  competitors,
  selectedCompetitors,
  onCompetitorChange,
}: AppTableProps<T>) {
  const [competitorDropdownOpen, setCompetitorDropdownOpen] = useState(false);
  const competitorDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!competitorDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        competitorDropdownRef.current &&
        !competitorDropdownRef.current.contains(e.target as Node)
      ) {
        setCompetitorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [competitorDropdownOpen]);

  const toggleCompetitor = (name: string) => {
    if (!onCompetitorChange || !selectedCompetitors) return;
    const next = selectedCompetitors.includes(name)
      ? selectedCompetitors.filter((c) => c !== name)
      : [...selectedCompetitors, name];
    onCompetitorChange(next);
  };

  const getSortIcon = (columnId: string) => {
    if (!onSort) return null;

    const isActive =
      (columnId === "percentage_difference" && sortBy === "diff") ||
      (columnId === "lowest_price" && sortBy === "lowest_price") ||
      (columnId === "last_price_updated_at" &&
        sortBy === "last_price_updated_at");

    if (isActive) {
      return sortOrder === "asc" ? (
        <ChevronUp
          size={16}
          style={{
            color: "#4fd1c5",
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
            strokeWidth: 2.5,
          }}
        />
      ) : (
        <ChevronDown
          size={16}
          style={{
            color: "#4fd1c5",
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
            strokeWidth: 2.5,
          }}
        />
      );
    }

    return (
      <ChevronsUpDown
        size={16}
        className="text-muted opacity-50"
        style={{ strokeWidth: 1.5 }}
      />
    );
  };

  const handleClick = (cellID: string, row: T) => {
    const value = (row as any)[cellID];

    if (onCellClick) {
      onCellClick(cellID, value, row);
    }
  };

  return (
    <div className="card shadow-sm border-0 rounded">
      <div className="card-header bg-white border-0 fw-bold fs-5 p-4 d-flex justify-content-between align-items-center gap-3">
        {title}
        <div className="table-filters d-flex flex-wrap justify-content-end gap-2 w-50">
          {onFilterChange ? (
            <select
              value={filterType}
              onChange={(e) =>
                onFilterChange(
                  e.target.value as
                    | "all"
                    | "not_cheapest"
                    | "price_changes"
                    | "watchlisted",
                )
              }
              className="form-select"
              style={{ maxWidth: "8rem", fontSize: "14px" }}
            >
              <option value="all">All Products</option>
              <option value="not_cheapest">Not Cheapest</option>
              <option value="price_changes">Today's Changes</option>
              <option value="watchlisted">Watchlisted Products</option>
            </select>
          ) : null}
          {onCompetitorChange && competitors && competitors.length > 0 ? (
            <div
              ref={competitorDropdownRef}
              style={{ position: "relative" }}
            >
              <button
                type="button"
                className="form-select text-start"
                style={{
                  maxWidth: "12rem",
                  fontSize: "14px",
                  cursor: "pointer",
                  backgroundColor: (selectedCompetitors?.length ?? 0) > 0 ? "#e6fafa" : undefined,
                  borderColor: (selectedCompetitors?.length ?? 0) > 0 ? "#4fd1c5" : undefined,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onClick={() => setCompetitorDropdownOpen((o) => !o)}
              >
                {(selectedCompetitors?.length ?? 0) === 0
                  ? "All Competitors"
                  : selectedCompetitors!.length === 1
                    ? selectedCompetitors![0]
                    : `${selectedCompetitors!.length} selected`}
              </button>

              {competitorDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    right: 0,
                    zIndex: 1000,
                    background: "#fff",
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    minWidth: "200px",
                    maxHeight: "280px",
                    overflowY: "auto",
                    padding: "4px 0",
                  }}
                >
                  {(selectedCompetitors?.length ?? 0) > 0 && (
                    <>
                      <button
                        type="button"
                        className="btn btn-link btn-sm w-100 text-start px-3 py-1"
                        style={{ fontSize: "12px", color: "#6c757d" }}
                        onClick={() => { onCompetitorChange([]); setCompetitorDropdownOpen(false); }}
                      >
                        Clear selection
                      </button>
                      <hr style={{ margin: "4px 0" }} />
                    </>
                  )}
                  {competitors.map((c) => {
                    const checked = selectedCompetitors?.includes(c.name) ?? false;
                    return (
                      <label
                        key={c.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "6px 12px",
                          cursor: "pointer",
                          fontSize: "14px",
                          backgroundColor: checked ? "#f0fffe" : "transparent",
                          userSelect: "none",
                        }}
                        onMouseEnter={(e) => { if (!checked) (e.currentTarget as HTMLElement).style.backgroundColor = "#f8f9fa"; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = checked ? "#f0fffe" : "transparent"; }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCompetitor(c.name)}
                          style={{ accentColor: "#4fd1c5" }}
                        />
                        {c.name}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
          {onSearchChange && (
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: "20rem" }}
              placeholder={searchPlaceholder}
              value={searchValue || ""}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          )}
        </div>
        {onGlobalToggleChange && handleEnableScraping && (
          <div className="header-actions d-flex flex-wrap">
            <AppSwitchButton
              checked={!!globalToggleChecked}
              onChange={(e) => onGlobalToggleChange(e.target.checked)}
            />

            <AppButton
              label="Scrape All"
              size="sm"
              fullWidth={false}
              onClick={handleEnableScraping}
            />
          </div>
        )}
      </div>

      <div
        className="card-body p-0 position-relative"
        style={{ minHeight: "300px" }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: "60%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <AppLoader />
          </div>
        )}
        {error && !loading && (
          <div className="p-4 text-center text-danger">{error}</div>
        )}

        {!loading && rows.length === 0 && !error && (
          <div className="p-4 text-center">{emptyText}</div>
        )}

        {!loading && rows.length > 0 && (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-4">
              <thead>
                <tr>
                  {columns.map((col) => {
                    const isActive = sortBy === col.id;

                    return (
                      <th
                        key={col.id}
                        scope="col"
                        className={`text-uppercase small px-3 user-select-none ${
                          col.sortable ? "sortable-header" : ""
                        } ${isActive ? "active-sort" : "text-muted"}`}
                        style={{
                          cursor: col.sortable ? "pointer" : "default",
                          fontWeight: isActive ? "600" : "500",
                          color: isActive ? "#4fd1c5" : undefined,
                          transition: "all 0.2s ease-in-out",
                          position: "relative",
                          padding: col.sortable ? "12px 16px" : "12px 16px",
                          borderRadius: col.sortable ? "6px" : "0",
                        }}
                        onClick={
                          col.sortable && onSort
                            ? () => onSort(col.id)
                            : undefined
                        }
                        onMouseEnter={(e) => {
                          if (col.sortable) {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                            e.currentTarget.style.transform =
                              "translateY(-1px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (col.sortable) {
                            e.currentTarget.style.backgroundColor = isActive
                              ? "#e7f1ff"
                              : "transparent";
                            e.currentTarget.style.transform = "translateY(0)";
                          }
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between gap-2 ">
                          <span>{col.label}</span>
                          {col.sortable && (
                            <div
                              className="sort-indicator d-flex align-items-center"
                              style={{
                                minWidth: "18px",
                                height: "18px",
                                borderRadius: "3px",
                                backgroundColor: isActive
                                  ? "rgba(13, 110, 253, 0.1)"
                                  : "transparent",
                                padding: "1px",
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              {getSortIcon(col.id)}
                            </div>
                          )}
                        </div>
                        {isActive && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "0",
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: "60%",
                              height: "2px",
                              backgroundColor: "#4fd1c5",
                              borderRadius: "2px",
                            }}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={renderRowKey(row)}
                    className="border-top table-row"
                    style={{ cursor: isRowClickable ? "pointer" : "default" }}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => {
                      const cellContent = col.render
                        ? col.render(row)
                        : (row as any)[col.id];
                      const wrapText = col.wrapText === true;
                      const cellTitle =
                        col.cellTitle?.(row) ??
                        (typeof cellContent === "string"
                          ? cellContent
                          : undefined);

                      return (
                        <td
                          key={col.id}
                          onClick={() => handleClick(col.id, row)}
                          className={`p-3 ${col.id === "errorCount" && "errorCount"}`}
                          style={
                            wrapText
                              ? {
                                  minWidth: "180px",
                                  maxWidth: "400px",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  verticalAlign: "top",
                                }
                              : {
                                  maxWidth: "200px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }
                          }
                          title={cellTitle}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        className="card-footer bg-white border-0 d-flex flex-column align-items-end"
        style={{ padding: "16px" }}
      >
        {lastRunDate != null && lastRunDate !== "" && (
          <div className="me-3 text-muted text-sm">Last Run: {lastRunDate}</div>
        )}

        <Pagination
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>
    </div>
  );
}

export default AppTable;
